import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { motion, AnimatePresence } from "framer-motion";
import {
  Eye, EyeOff, Leaf, ArrowRight, Lock, Mail,
  Recycle, Shield, Truck, BarChart3, Sparkles, MapPin
} from "lucide-react";
import ForgotPasswordModal from "../components/ForgotPasswordModal";

/* ─── Animated counter (count up on mount) ─────────────────────────────── */
function Counter({ to = 0, duration = 1200, suffix = "" }) {
  const [n, setN] = useState(0);
  useEffect(() => {
    let raf;
    const start = performance.now();
    const step = (t) => {
      const p = Math.min(1, (t - start) / duration);
      // ease-out
      const eased = 1 - Math.pow(1 - p, 3);
      setN(Math.round(to * eased));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [to, duration]);
  return <>{n.toLocaleString("es-ES")}{suffix}</>;
}

/* ─── Floating leaves background (CSS keyframes) ───────────────────────── */
const Leaves = () => {
  const items = Array.from({ length: 14 }).map((_, i) => ({
    left: `${(i * 7.7 + 3) % 100}%`,
    delay: `${(i * 1.3) % 9}s`,
    duration: `${10 + (i % 5) * 2}s`,
    size: 12 + (i % 4) * 6,
    rotate: (i * 31) % 360,
    opacity: 0.18 + ((i * 7) % 30) / 100,
  }));
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden" data-testid="leaves-fx">
      {items.map((it, i) => (
        <Leaf
          key={i}
          size={it.size}
          className="absolute text-emerald-300 will-change-transform"
          style={{
            left: it.left,
            top: "-30px",
            opacity: it.opacity,
            transform: `rotate(${it.rotate}deg)`,
            animation: `leafFall ${it.duration} linear ${it.delay} infinite`,
            filter: "drop-shadow(0 0 8px rgba(74,222,128,0.4))",
          }}
        />
      ))}
    </div>
  );
};

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [forgotOpen, setForgotOpen] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(email, password);
      if (u?.must_change_password) {
        navigate("/change-password");
      } else {
        navigate("/");
      }
    } catch (err) {
      setError("Credenciales incorrectas. Verifica email y contraseña.");
    } finally {
      setLoading(false);
    }
  };

  const stats = [
    { v: 12, l: "Contratos activos", icon: MapPin },
    { v: 380, suffix: "+", l: "Operarios", icon: Truck },
    { v: 99, suffix: "%", l: "Trazabilidad IA", icon: BarChart3 },
  ];

  return (
    <div className="min-h-screen flex bg-[#04140a] text-white selection:bg-emerald-400/40">
      {/* Local keyframes & utilities */}
      <style>{`
        @keyframes leafFall {
          0%   { transform: translate3d(0,-40px,0) rotate(0deg); }
          50%  { transform: translate3d(40px, 50vh, 0) rotate(180deg); }
          100% { transform: translate3d(-20px, 110vh, 0) rotate(360deg); }
        }
        @keyframes kenBurns {
          0%   { transform: scale(1.0) translate3d(0,0,0); }
          50%  { transform: scale(1.12) translate3d(-2%, -1%, 0); }
          100% { transform: scale(1.0) translate3d(0,0,0); }
        }
        @keyframes pulseRing {
          0%   { box-shadow: 0 0 0 0 rgba(74,222,128,0.55); }
          70%  { box-shadow: 0 0 0 22px rgba(74,222,128,0); }
          100% { box-shadow: 0 0 0 0 rgba(74,222,128,0); }
        }
        @keyframes shimmerBorder {
          0%   { background-position: 0% 50%; }
          100% { background-position: 200% 50%; }
        }
        @keyframes floatY {
          0%,100% { transform: translateY(0px); }
          50%     { transform: translateY(-10px); }
        }
        .glass-card {
          background: linear-gradient(135deg, rgba(255,255,255,0.10) 0%, rgba(255,255,255,0.04) 100%);
          backdrop-filter: blur(20px) saturate(140%);
          -webkit-backdrop-filter: blur(20px) saturate(140%);
        }
        .gradient-border {
          position: relative;
          background-clip: padding-box;
        }
        .gradient-border::before {
          content: "";
          position: absolute; inset: 0;
          border-radius: inherit;
          padding: 1.5px;
          background: linear-gradient(120deg, #3BAD2D, #00d09c, #007934, #00ffae, #3BAD2D);
          background-size: 200% 100%;
          animation: shimmerBorder 6s linear infinite;
          -webkit-mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
          -webkit-mask-composite: xor;
                  mask-composite: exclude;
          pointer-events: none;
        }
      `}</style>

      {/* ════════ LEFT PANEL ════════════════════════════════════════════════ */}
      <div className="hidden lg:flex lg:w-[58%] relative overflow-hidden">
        {/* Background image with Ken Burns */}
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1532996122724-e3c354a0b15b?w=1600&q=85&fit=crop')",
            backgroundSize: "cover",
            backgroundPosition: "center",
            animation: "kenBurns 22s ease-in-out infinite",
          }}
        />
        {/* Gradient overlays */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#001f0f]/95 via-[#003c1d]/85 to-[#007934]/55" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(0,255,174,0.18),transparent_45%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_85%,rgba(59,173,45,0.25),transparent_50%)]" />

        {/* Falling leaves */}
        <Leaves />

        {/* Animated grid pattern */}
        <div
          className="absolute inset-0 opacity-[0.07]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.4) 1px, transparent 1px)",
            backgroundSize: "44px 44px",
          }}
        />

        {/* Decorative recycling GIF as a glowing badge */}
        <motion.div
          initial={{ opacity: 0, scale: 0.4, rotate: -30 }}
          animate={{ opacity: 1, scale: 1, rotate: 0 }}
          transition={{ delay: 0.6, duration: 0.9, type: "spring", stiffness: 90 }}
          className="absolute top-10 right-10 w-28 h-28 rounded-full overflow-hidden ring-2 ring-emerald-300/40 shadow-[0_0_60px_rgba(74,222,128,0.45)]"
          style={{ animation: "pulseRing 3s ease-out infinite, floatY 6s ease-in-out infinite" }}
          data-testid="login-eco-gif"
        >
          <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/30 to-transparent z-10 mix-blend-overlay" />
          <img
            src="https://media.giphy.com/media/ZI4owdgI8ob1LtkFjt/giphy.gif"
            alt="Reduce, reuse, recycle"
            className="w-full h-full object-cover"
            loading="eager"
          />
        </motion.div>

        {/* Content */}
        <div className="relative z-10 flex flex-col justify-between p-14 w-full">
          {/* Top: logo */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <svg viewBox="0 0 1047.16 256.38" className="h-11 mb-1">
              <path fill="#FFFFFF" d="M341.5,72.42l-30.94,0l-26.39,69.9l-26.13-69.9h-31.79l42.64,107.76l1.28,3.23h23.97l1.28-3.23Z"/>
              <path fill="#FFFFFF" d="M383.11,72.42l-31.13,0l24.64,57.27h-22.33v26.77h33.65l2.38,5.64l8.92,21.31h31.91Z"/>
              <path fill="#FFFFFF" d="M470.98,72.42h-29.51v110.99h72.98v-26.95h-43.47Z"/>
              <path fill="#FFFFFF" d="M575.63,72.53c-10.53,0-20.29,2.43-28.99,7.21c-8.75,4.81-15.73,11.51-20.75,19.92c-5.03,8.42-7.58,17.87-7.58,28.1c0,10.22,2.55,19.69,7.58,28.16c5.01,8.44,11.98,15.21,20.72,20.12c8.72,4.89,18.48,7.37,29.03,7.37c10.55,0,20.32-2.48,29.04-7.37c8.73-4.9,15.71-11.67,20.72-20.12c5.03-8.47,7.58-17.94,7.58-28.16c0-10.22-2.55-19.67-7.58-28.1c-5.03-8.41-12.01-15.11-20.75-19.92C595.92,74.96,586.16,72.53,575.63,72.53ZM575.91,156.46c-5.14,0-9.75-1.24-14.11-3.81c-4.4-2.58-7.82-6-10.44-10.45c-2.61-4.41-3.87-9.13-3.87-14.45c0-5.32,1.24-10.03,3.81-14.39c2.57-4.38,5.97-7.74,10.39-10.27c4.42-2.53,9.07-3.76,14.23-3.76c5.04,0,9.59,1.22,13.91,3.74c4.33,2.53,7.67,5.88,10.2,10.26c2.52,4.37,3.75,9.09,3.75,14.42c0,5.34-1.23,10.09-3.76,14.51c-2.54,4.43-5.88,7.83-10.22,10.4C585.48,155.22,580.94,156.46,575.91,156.46Z"/>
              <rect fill="#FFFFFF" x="751.45" y="72.42" width="29.51" height="110.99"/>
              <path fill="#FFFFFF" d="M894.82,72.42h-95.59v27.06h53.42l-53.42,63.27v20.66h96.31v-27.07h-54.16l53.44-63.41Z"/>
              <path fill="#FFFFFF" d="M735.08,183.41l-23.05-34.73c10.7-7.62,17.68-20.11,17.68-34.24c0-23.21-18.81-42.02-42.02-42.02h-40.1v26.91h40.1c8.38,0,15.18,6.79,15.18,15.18s-6.79,15.18-15.18,15.18h-40.1v26.77h37.26l15.75,26.95Z"/>
              <path fill="#FFFFFF" d="M941.52,72.42l-31.13,0l24.64,57.27h-22.33v26.77h33.65l2.38,5.64l8.92,21.31h31.91Z"/>
              <path fill="#3BAD2D" d="M123.05,99.03v-41.9H109.1v27.94L89.35,65.31l-9.86,9.86l29.63,29.63C113.08,101.75,117.85,99.7,123.05,99.03"/>
              <path fill="#3BAD2D" d="M103.85,110.07L74.22,80.44l-9.86,9.86l19.75,19.75H56.17v13.95h41.91C98.75,118.8,100.8,114.03,103.85,110.07"/>
              <path fill="#3BAD2D" d="M155.48,124.01h41.91v-13.95h-27.94l19.75-19.75l-9.86-9.86l-29.63,29.63C152.76,114.03,154.81,118.8,155.48,124.01"/>
              <path fill="#3BAD2D" d="M144.44,104.8l29.63-29.63l-9.86-9.86l-19.75,19.75V57.13h-13.95v41.91C135.71,99.7,140.48,101.75,144.44,104.8"/>
              <path fill="#3BAD2D" d="M98.08,131.46H56.17v13.95H84.1l-19.75,19.75l9.86,9.86l29.64-29.64C100.8,141.43,98.75,136.66,98.08,131.46"/>
              <path fill="#3BAD2D" d="M149.7,145.39l29.64,29.64l9.86-9.86l-19.75-19.75h27.94v-13.95h-41.91C154.81,136.66,152.76,141.43,149.7,145.39"/>
              <path fill="#3BAD2D" d="M109.12,150.66L79.49,180.3l9.86,9.86l19.75-19.75v27.94h13.95v-41.92C117.85,155.76,113.08,153.71,109.12,150.66"/>
              <path fill="#3BAD2D" d="M130.51,156.43v41.92h13.95v-27.94l19.75,19.75l9.86-9.86l-29.64-29.64C140.48,153.71,135.71,155.76,130.51,156.43"/>
            </svg>
            <div className="flex items-center gap-2">
              <span className="inline-flex h-1.5 w-1.5 rounded-full bg-emerald-300 animate-pulse" />
              <p className="text-emerald-100/70 text-[13px] font-medium tracking-[0.18em] uppercase">
                Plataforma de Gestión Operacional
              </p>
            </div>
          </motion.div>

          {/* Middle: hero */}
          <div className="space-y-7 max-w-2xl">
            <motion.h2
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.7 }}
              className="text-white text-[44px] leading-[1.05] font-extrabold tracking-tight"
            >
              Gestionamos lo que <span className="bg-gradient-to-r from-emerald-300 via-green-200 to-lime-300 bg-clip-text text-transparent">cuida el planeta</span>.
            </motion.h2>

            <motion.p
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.35, duration: 0.7 }}
              className="text-emerald-50/85 text-base leading-relaxed max-w-lg"
            >
              Centraliza partes, tickets de pesaje y combustible, KPIs y alertas de tus
              servicios medioambientales — con extracción documental por <b className="text-emerald-200">IA Claude Sonnet 4.6</b>.
            </motion.p>

            {/* Feature pills */}
            <motion.div
              initial="hidden"
              animate="show"
              variants={{ hidden: {}, show: { transition: { staggerChildren: 0.08, delayChildren: 0.5 } } }}
              className="flex flex-wrap gap-2.5"
            >
              {[
                { icon: Recycle, t: "Trazabilidad documental" },
                { icon: Sparkles, t: "Análisis con IA" },
                { icon: BarChart3, t: "KPIs en tiempo real" },
                { icon: Shield, t: "Aislamiento por contrato" },
              ].map(({ icon: Icon, t }, i) => (
                <motion.span
                  key={i}
                  variants={{ hidden: { opacity: 0, y: 10 }, show: { opacity: 1, y: 0 } }}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold
                             bg-white/8 border border-white/15 text-emerald-50 backdrop-blur-sm
                             hover:bg-white/15 hover:border-emerald-300/40 transition-all"
                >
                  <Icon size={14} className="text-emerald-300" /> {t}
                </motion.span>
              ))}
            </motion.div>

            {/* Live counters */}
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7, duration: 0.7 }}
              className="grid grid-cols-3 gap-3 max-w-md"
            >
              {stats.map(({ v, suffix, l, icon: Icon }) => (
                <div
                  key={l}
                  className="relative gradient-border rounded-2xl p-4 glass-card"
                >
                  <Icon size={16} className="text-emerald-300 mb-2" />
                  <p className="text-white text-2xl font-extrabold tabular-nums">
                    <Counter to={v} suffix={suffix || ""} />
                  </p>
                  <p className="text-emerald-100/60 text-[11px] uppercase tracking-wide mt-0.5">
                    {l}
                  </p>
                </div>
              ))}
            </motion.div>
          </div>

          {/* Bottom: footer */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1, duration: 0.8 }}
            className="flex items-center justify-between"
          >
            <p className="text-emerald-100/40 text-xs">
              Powered by Valoriza Innovation Team · {new Date().getFullYear()}
            </p>
            <div className="flex items-center gap-1.5 text-emerald-100/40 text-xs">
              <span className="inline-flex h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
              valorizasm.com
            </div>
          </motion.div>
        </div>
      </div>

      {/* ════════ RIGHT PANEL ═══════════════════════════════════════════════ */}
      <div className="flex-1 flex items-center justify-center p-8 bg-gradient-to-br from-[#06150c] via-[#091f12] to-[#04140a] relative overflow-hidden">
        {/* Subtle background blobs */}
        <div className="absolute -top-32 -right-24 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl" />
        <div className="absolute -bottom-24 -left-24 w-80 h-80 bg-green-700/10 rounded-full blur-3xl" />

        <motion.div
          initial={{ opacity: 0, y: 20, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
          className="w-full max-w-md relative z-10"
        >
          {/* Mobile logo */}
          <div className="lg:hidden mb-8 text-center">
            <motion.div
              animate={{ rotate: [0, -5, 5, 0] }}
              transition={{ duration: 4, repeat: Infinity }}
              className="inline-flex items-center justify-center w-16 h-16 bg-emerald-500 rounded-2xl mb-4 shadow-[0_8px_30px_rgba(16,185,129,0.4)]"
            >
              <Leaf size={32} className="text-white" />
            </motion.div>
          </div>

          <div className="gradient-border rounded-3xl glass-card p-8 shadow-[0_20px_60px_-15px_rgba(0,0,0,0.6)]">
            <div className="mb-7">
              <div className="flex items-center gap-2 mb-3">
                <span className="inline-flex h-2 w-2 rounded-full bg-emerald-400 animate-pulse" />
                <span className="text-emerald-300 text-[11px] font-bold tracking-[0.22em] uppercase">
                  Acceso seguro
                </span>
              </div>
              <h1 className="text-3xl font-extrabold text-white mb-1.5 tracking-tight">
                Bienvenido de vuelta
              </h1>
              <p className="text-emerald-100/60 text-sm">
                Inicia sesión para acceder a tu panel operativo.
              </p>
            </div>

            <AnimatePresence>
              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -8, height: 0 }}
                  animate={{ opacity: 1, y: 0, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  data-testid="login-error"
                  className="mb-4 p-3.5 bg-red-500/10 border border-red-400/30 rounded-xl text-red-300 text-sm flex items-center gap-2"
                >
                  <span className="w-5 h-5 flex-shrink-0 inline-flex items-center justify-center rounded-full bg-red-500/20 text-red-300 text-xs font-bold">!</span>
                  {error}
                </motion.div>
              )}
            </AnimatePresence>

            <form onSubmit={handleSubmit} className="space-y-4" data-testid="login-form">
              <div>
                <label className="block text-xs font-bold text-emerald-100/80 mb-2 tracking-wider uppercase">
                  Email corporativo
                </label>
                <div className="relative group">
                  <Mail size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-emerald-300/70 group-focus-within:text-emerald-300 transition-colors" />
                  <input
                    data-testid="email-input"
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    className="w-full pl-10 pr-4 py-3.5 bg-white/5 border border-white/10 rounded-xl text-sm text-white placeholder-white/30
                               focus:outline-none focus:ring-2 focus:ring-emerald-400/40 focus:border-emerald-400/60 transition-all"
                    placeholder="tu@valoriza.com"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-emerald-100/80 mb-2 tracking-wider uppercase">
                  Contraseña
                </label>
                <div className="relative group">
                  <Lock size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-emerald-300/70 group-focus-within:text-emerald-300 transition-colors" />
                  <input
                    data-testid="password-input"
                    type={showPass ? "text" : "password"}
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    className="w-full pl-10 pr-10 py-3.5 bg-white/5 border border-white/10 rounded-xl text-sm text-white placeholder-white/30
                               focus:outline-none focus:ring-2 focus:ring-emerald-400/40 focus:border-emerald-400/60 transition-all"
                    placeholder="••••••••"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPass(!showPass)}
                    className="absolute right-3.5 top-1/2 -translate-y-1/2 text-emerald-300/70 hover:text-emerald-300 transition-colors"
                    data-testid="toggle-password-btn"
                  >
                    {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              <motion.button
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.97 }}
                data-testid="login-submit-btn"
                type="submit"
                disabled={loading}
                className="w-full flex items-center justify-center gap-2 mt-2
                           bg-gradient-to-r from-emerald-500 to-green-500 hover:from-emerald-400 hover:to-green-400
                           text-white font-bold py-3.5 rounded-xl transition-all
                           shadow-[0_10px_30px_-10px_rgba(16,185,129,0.6)]
                           disabled:opacity-60 disabled:cursor-not-allowed relative overflow-hidden group"
              >
                <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
                {loading ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <>Acceder al panel <ArrowRight size={16} /></>
                )}
              </motion.button>

              <div className="text-center pt-1">
                <button
                  type="button"
                  onClick={() => setForgotOpen(true)}
                  data-testid="forgot-password-link"
                  className="text-xs text-emerald-300/70 hover:text-emerald-300 font-semibold transition-colors"
                >
                  ¿Has olvidado tu contraseña?
                </button>
              </div>
            </form>
          </div>

          <p className="text-center text-xs text-emerald-100/40 mt-6">
            Powered by <span className="text-emerald-300/70 font-semibold">Valoriza Innovation Team</span>
          </p>
        </motion.div>
      </div>

      <ForgotPasswordModal open={forgotOpen} onClose={() => setForgotOpen(false)} />
    </div>
  );
}
